import { changeDateFormat } from '../../../Custom/Custom';
import { IIPIndexReport } from './ExportIIPExcel';

const MONTH_ORDER = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

const formatMonthYearShort = (m, y) => {
  const monthText = String(m || '').trim().toUpperCase();
  const monthLabel = monthText ? `${monthText.charAt(0)}${monthText.slice(1).toLowerCase()}` : 'NA';
  const yearText = String(y || '');
  const yy = yearText.length >= 2 ? yearText.slice(-2) : yearText;
  return `${monthLabel}-${yy}`;
};

const getPreviousMonthYear = (m, y) => {
  const monthText = String(m || '').trim().toUpperCase();
  const yearNum = parseInt(y, 10);
  const monthIndex = MONTH_ORDER.indexOf(monthText);
  if (monthIndex === -1 || !Number.isFinite(yearNum)) {
    return { month: monthText || '', year: String(y || '') };
  }
  if (monthIndex === 0) return { month: 'DEC', year: String(yearNum - 1) };
  return { month: MONTH_ORDER[monthIndex - 1], year: String(yearNum) };
};

const parseNumber = (val) => {
  if (val === null || val === undefined || val === '') return null;
  const num = Number(val);
  return Number.isFinite(num) ? num : null;
};

const getVariationPercent = (currentIndexRaw, referenceIndexRaw) => {
  const current = parseNumber(currentIndexRaw);
  const reference = parseNumber(referenceIndexRaw);
  if (current === null || reference === null) return '';
  let variation = 0;
  if (reference === 0) variation = current === 0 ? 0 : 100;
  else variation = ((current - reference) / reference) * 100;
  if (Math.abs(variation) < 1e-9) variation = 0;
  return `${variation.toFixed(2)}%`;
};

const mapIndexApiToExcelRows = (monthlyList) => {
  const allData = Array.isArray(monthlyList)
    ? monthlyList.reduce((acc, data) => acc.concat(data.Items || []), [])
    : [];
  let runningSNo = 0;
  return allData.map((item) => {
    const nic5d = item.nic5d ?? item.NIC5d ?? item.NIC5D ?? '';
    const hasNic5d = String(nic5d ?? '').trim() !== '';
    const sNo = hasNic5d ? ++runningSNo : '';
    const currentIndex = item.index ?? item.IndexI ?? '';
    const prevMonthIndex = item.previousMonthData?.IndexI ?? '';
    const prevYearIndex = item.previousYearData?.IndexI ?? '';
    return {
      SNo: sNo,
      NIC3d: item.nic3d ?? item.NIC3d ?? item.NIC3D ?? '',
      NIC4d: item.nic4d ?? item.NIC4d ?? item.NIC4D ?? '',
      NIC5d: nic5d,
      Item: item.item ?? item.Item ?? '',
      NPCMSCode: item.npcms_code ?? item.npcmsCode ?? item.NPCMS_CODE ?? item.NPCMSCode ?? '',
      NIC2d: item.nic2d ?? item.NIC2d ?? item.NIC2D ?? '',
      ItemName: item.item_name ?? item.itemName ?? item.ItemName ?? '',
      Weight: item.weight ?? item.Weight ?? '',
      Units: item.unit_name ?? item.unitName ?? item.Units ?? '',
      BPQty: item.base_year_price ?? item.bp ?? item.BPQty ?? '',
      CPQty: item.cp ?? item.CPQty ?? '',
      IndexI: currentIndex,
      IndexWt: item.index_wt ?? item.indexWt ?? item.IndexWt ?? '',
      PrevMonthBPQty: item.previousMonthData?.BPQty ?? '',
      PrevMonthCPQty: item.previousMonthData?.CPQty ?? '',
      PrevMonthIndexI: item.previousMonthData?.IndexI ?? '',
      PrevMonthIndexWt: item.previousMonthData?.IndexWt ?? '',
      PrevMonthVariation: getVariationPercent(currentIndex, prevMonthIndex),
      PrevYearBPQty: item.previousYearData?.BPQty ?? '',
      PrevYearCPQty: item.previousYearData?.CPQty ?? '',
      PrevYearIndexI: item.previousYearData?.IndexI ?? '',
      PrevYearIndexWt: item.previousYearData?.IndexWt ?? '',
      PrevYearVariation: getVariationPercent(currentIndex, prevYearIndex),
      highlightInjected: item.isInjected === true,
    };
  });
};

export const downloadFrQrIndexExcel = async (monthlyList, month, year, fileNameOverride) => {
  const reportMonth = String(month || '').trim().toUpperCase();
  const reportYear = String(year || '').trim();
  const reportData = mapIndexApiToExcelRows(monthlyList);
  if (!reportData.length) {
    throw new Error('No data found for report.');
  }

  const currentLabel = formatMonthYearShort(reportMonth, reportYear);
  const prevMonthInfo = getPreviousMonthYear(reportMonth, reportYear);
  const previousMonthLabel = formatMonthYearShort(prevMonthInfo.month, prevMonthInfo.year);
  const previousYearLabel = formatMonthYearShort(
    reportMonth,
    String((parseInt(reportYear, 10) || 0) - 1)
  );

  const excelObjectData = {
    fileName: fileNameOverride || `IndexReport_Monthly_${reportMonth}_${reportYear}`,
    reportType: `IndexReport_Monthly_${reportMonth}_${reportYear}`,
    fileType: 'xls',
    sheetName: 'IIPIndexReport',
    previousMonthHighlightColor: 'ADD8E6',
    previousYearHighlightColor: 'FFE4B5',
    firstRowMergeCells: 'A1:V1',
    firstRowExportData: changeDateFormat(new Date()),
    firstRowCellNumber: 22,
    secondRowMergeCells: 'A2:V2',
    secondRowText: 'ECONOMICS & STATISTICS DIVISION, UTTAR PRADESH',
    thirdRowMergeCells: 'A3:V3',
    thirdRowText: 'MANUFACTURING SECTOR',
    fourthRowMergeCells: 'A4:V4',
    fourthRowText: `Monthly Report - ${reportMonth} (${reportYear})`,
    fifthRowMergeCells: 'A5:V5',
    fifthRowText: 'MANUFACTURING SECTOR',
    headerEnglishColumns: [
      'S.No', 'NIC-3d', 'NIC-4d', 'NIC-5d', 'Item', 'NPCMS Code', 'NIC-2d', 'Item Name', 'Weight', 'Units', 'B.P.(Qty)', 'C.P.(Qty)', 'Index(I)', 'Index* Wt'
    ],
    groupedHeader: {
      fixedHeaders: ['S.No', 'NIC-3d', 'NIC-4d', 'NIC-5d', 'Item', 'NPCMS Code', 'NIC-2d', 'Item Name', 'Weight', 'Units'],
      groups: [
        { title: currentLabel, columns: ['B.P.(Qty)', 'C.P.(Qty)', 'Index(I)', 'Index* Wt'] },
        { title: previousMonthLabel, columns: ['B.P.(Qty)', 'C.P.(Qty)', 'Index(I)', 'Index* Wt', 'Variation %'] },
        { title: previousYearLabel, columns: ['B.P.(Qty)', 'C.P.(Qty)', 'Index(I)', 'Index* Wt', 'Variation %'] },
      ],
    },
    excelRowData: reportData.map((item) => [
      item.SNo,
      item.NIC3d,
      item.NIC4d,
      item.NIC5d,
      item.Item,
      item.NPCMSCode,
      item.NIC2d,
      item.ItemName,
      item.Weight,
      item.Units,
      item.BPQty,
      item.CPQty,
      item.IndexI,
      item.IndexWt,
      item.PrevMonthBPQty,
      item.PrevMonthCPQty,
      item.PrevMonthIndexI,
      item.PrevMonthIndexWt,
      item.PrevMonthVariation,
      item.PrevYearBPQty,
      item.PrevYearCPQty,
      item.PrevYearIndexI,
      item.PrevYearIndexWt,
      item.PrevYearVariation,
    ]),
    rowHighlightFlags: reportData.map((item) => item.highlightInjected === true),
    injectedHighlightColor: 'FFFF99',
  };

  await IIPIndexReport(excelObjectData);
};
